import { isNumber } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import usePageMeta from 'hooks/use-page-meta';
import DataTable from 'pages/Search/components/DataTable';
import Layout from 'pages/Search/components/Layout';
import LetterFilter from 'pages/Search/components/LetterFilter';
import RedirectLink from 'pages/Search/components/RedirectLink';
import SearchInput from 'pages/Search/components/SearchInput';
import SectionHeading from 'pages/Search/components/SectionHeading';
import { useVendorProductCatalogPayload } from 'pages/Search/components/Vendor/hooks/use-vendor-product-catalog-payload';
import { ROUTES } from 'pages/Search/constants';
import { normalizeName } from 'pages/Search/utils';
import { useProductsCatalog } from 'queries/catalog/use-products-catalog';
import Utils from 'utils/Utils';

import useStyles from './styles';

const Vendor: React.FC = () => {
    const { classes } = useStyles();

    const layoutRef = useRef<{ scrollTop: VoidFunction } | null>(null);
    const [payload, actions] = useVendorProductCatalogPayload();
    const { data, isLoading } = useProductsCatalog(payload);

    useEffect(() => {
        if (data && layoutRef.current) {
            layoutRef.current.scrollTop();
        }
    }, [data]);

    const hasMetaPagination = payload.page > 1;
    const vendorName = normalizeName(payload.vendor);

    usePageMeta({
        title: hasMetaPagination
            ? `${vendorName} vulnerabilities - Page ${payload.page} | Vulners.com`
            : `${vendorName} vulnerabilities | Vulners.com`,
        description: hasMetaPagination
            ? `${vendorName} - Page ${payload.page}. Here you can find all recent ${vendorName} security vulnerabilities from different products`
            : `Here you can find all recent ${vendorName} security vulnerabilities from different products`
    });

    const getOptionHref = useCallback((letter: string) => actions.url({ letter }), [actions]);
    const getPaginationHref = useCallback((page: number) => actions.url({ page }), [actions]);

    const tableHeaders = useMemo(
        () => [
            { field: 'product' as const, label: 'Products', sort: true },
            { field: 'cves' as const, label: 'Vulnerabilities', sort: true, width: 120 }
        ],
        []
    );

    const tableData = useMemo(
        () =>
            data?.items.map(({ vendor, product, cves }, index) => [
                <RedirectLink
                    key={`product-${index}`}
                    to={ROUTES.PRODUCT_VERSIONS({
                        page: null,
                        vendor: vendor,
                        product: product,
                        score: payload.score,
                        year: payload.year,
                        month: payload.month
                    })}
                    label={normalizeName(product)}
                    active={!!vendor && !!product}
                    className={classes.link}
                />,
                <RedirectLink
                    key={`cves-${index}`}
                    to={ROUTES.PRODUCT_CVES({
                        page: null,
                        vendor: vendor,
                        product: product,
                        score: payload.score,
                        year: payload.year,
                        month: payload.month
                    })}
                    label={cves}
                    active={!!vendor && !!product && cves > 0}
                    className={classes.link}
                />
            ]) ?? [],
        [classes.link, data?.items, payload.month, payload.score, payload.year]
    );

    const handlePageChange = useCallback((page: number) => actions.set({ page }), [actions]);
    const handlePageSizeChange = useCallback((size: number) => actions.set({ size }), [actions]);
    const handleLetterSelect = useCallback((letter: string) => actions.set({ letter }), [actions]);

    return (
        <Layout
            scrollable
            ref={layoutRef}
            main={
                <div className={classes.content}>
                    <SectionHeading
                        title={[
                            vendorName,
                            isNumber(payload.score) ? `CVSS Score ${payload.score} - ${payload.score + 1}` : null,
                            payload.year?.toString(),
                            isNumber(payload.month) ? Utils.monthOfYear(payload.month - 1) : null
                        ]}
                        total={data?.total ?? NaN}
                        breadcrumbs={[
                            { to: ROUTES.VENDORS(), label: 'Vendors' },
                            { to: ROUTES.VENDOR({ vendor: payload.vendor }), label: vendorName },
                            isNumber(payload.score)
                                ? {
                                      to: ROUTES.VENDOR({ vendor: payload.vendor, score: payload.score }),
                                      label: `CVSS Score ${payload.score} - ${payload.score + 1}`
                                  }
                                : null,
                            isNumber(payload.year)
                                ? {
                                      to: ROUTES.VENDOR({
                                          vendor: payload.vendor,
                                          year: payload.year
                                      }),
                                      label: payload.year.toString()
                                  }
                                : null,
                            isNumber(payload.month)
                                ? {
                                      to: ROUTES.VENDOR({
                                          vendor: payload.vendor,
                                          year: payload.year,
                                          month: payload.month
                                      }),
                                      label: Utils.monthOfYear(payload.month - 1)
                                  }
                                : null
                        ]}
                    />
                    <SearchInput
                        value={payload.search}
                        placeholder="Search by product name"
                        onChange={(value) => actions.set({ search: value ?? undefined })}
                    />
                    <LetterFilter
                        total={data?.total ?? NaN}
                        selected={payload.letter}
                        getOptionHref={getOptionHref}
                        onSelect={handleLetterSelect}
                    />
                    <DataTable
                        headers={tableHeaders}
                        data={tableData}
                        loading={isLoading}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        getPaginationHref={getPaginationHref}
                        onSortingChange={(sort) => actions.set({ sort })}
                        page={payload.page}
                        pageSize={payload.size}
                        totalCount={data?.total}
                        sorting={payload.sort}
                    />
                </div>
            }
        />
    );
};

export default Vendor;
